/**
 * useEndSession hook
 *
 * React Query mutation hook wrapping the endEnglishSession() service (TS-007 T2,
 * API-001). On confirm it POSTs /sessions/{sessionId}/end and, on success,
 * invalidates every parent-facing cache that could show the now-terminated live
 * session so it disappears everywhere on next read: the Overview dashboard, the
 * dedicated live-sessions listing page, the Reports tab, and the student's own
 * session list.
 *
 * Implements TS-007 T3 (AC-TS007.3).
 * Cloned from useCancelSession (hooks/useCancelSession.ts) with three deliberate
 * differences: (1) it calls endEnglishSession instead of cancelSession, (2) its
 * invalidation set targets the parent surfaces (dashboard/live-sessions/sessions/
 * student sessions) rather than a single session's messages cache, and (3) per
 * TO-LL-013 it does NOT navigate anywhere on success — the parent stays on
 * whichever page (Overview or the listing) they were on; the card simply
 * disappears once the invalidated queries refetch.
 *
 * KB enforcement:
 *   TO-LL-036 — end failures are NEVER swallowed. onError is intentionally absent;
 *               React Query surfaces mutation.error raw so the confirmation dialog
 *               (EndSessionConfirmDialog) can show an inline retry message. On
 *               failure we do NOT invalidate and do NOT navigate — the dialog stays
 *               open for retry.
 *   TO-LL-013 — single navigation source: this hook deliberately has NO useNavigate
 *               call (unlike its clone source useCancelSession). The interactive
 *               card (T8) and listing page (T9) must not add a second navigate on
 *               end success either — they rely on cache invalidation only.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { endEnglishSession } from '@/services/sessions';
import type { AuthError } from '@/types/auth';

/**
 * Return type for useEndSession hook
 */
export interface UseEndSessionReturn {
  /** Trigger the end-session mutation */
  endSession: () => void;

  /** True while the end POST is in flight */
  isEnding: boolean;

  /**
   * Error from a failed end attempt, or null.
   * Exposed so the confirmation dialog can show an inline retry message.
   * Implements TO-LL-036: error is never swallowed.
   */
  error: AuthError | null;

  /** Reset the mutation state (clear error, allow retry) */
  reset: () => void;
}

/**
 * Hook to force-end a live English tutoring session (parent-controlled).
 *
 * On success:
 *   1. Invalidates ['parent','dashboard'] — clears the Overview live card + child
 *      card's lastSessionStatus.
 *   2. Invalidates ['parent','live-sessions'] — refreshes the dedicated listing page.
 *   3. Invalidates ['parent','sessions'] — the Reports tab picks up the ended row.
 *   4. Invalidates ['sessions'] — the student's own session list reflects the
 *      terminal status on next read.
 *   TO-LL-013: no navigate() call — the parent stays on the current page.
 *
 * On failure:
 *   - Exposes error via .error; does NOT navigate and does NOT invalidate (TO-LL-036).
 *   - The caller (EndSessionConfirmDialog) keeps the dialog open for retry.
 *
 * @param sessionId - The live session ID to end
 */
export function useEndSession(sessionId: string): UseEndSessionReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    { sessionId: string; status: 'ended_by_parent'; updatedAt: string },
    AuthError,
    void
  >({
    mutationFn: () => endEnglishSession(sessionId),

    onSuccess: () => {
      // Overview dashboard: live card, child card lastSessionStatus, liveSessions[] embed
      queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
      // Dedicated live-sessions listing page (T9)
      queryClient.invalidateQueries({ queryKey: ['parent', 'live-sessions'] });
      // Reports tab — the ended session should appear there too
      queryClient.invalidateQueries({ queryKey: ['parent', 'sessions'] });
      // Student's own session list — reflects the terminal status on next read
      queryClient.invalidateQueries({ queryKey: ['sessions'] });

      // TO-LL-013: no navigate() here — single navigation source discipline means
      // this hook never redirects the parent; the card disappears once the
      // invalidated queries above refetch.
    },

    // TO-LL-036: onError is intentionally absent — React Query surfaces the error
    // via mutation.error automatically. We do NOT provide an onError that swallows
    // or navigates-on-failure. The error flows back to the caller as-is.
  });

  return {
    endSession: () => mutation.mutate(),
    isEnding: mutation.isPending,
    // TO-LL-036: expose raw error — never null-coalesce to a fallback that hides it
    error: (mutation.error as AuthError | null) ?? null,
    reset: mutation.reset,
  };
}
